import { templateByKey } from './marketingOptions'
import { addDays, fromIso, iso, today } from '../../core/format/dates'
import type { MarketingDesign } from '../../core/types/marketing'
import type { SocialPost } from '../../core/types/social'

/**
 * De brug tussen Ontwerpen en de Social planner: één plek voor "dit ontwerp als post"
 * en "staat dit ontwerp al in de planning?". De editor, het ontwerpenoverzicht en de planner
 * gebruiken allemaal deze regels.
 */

/** Alleen ontwerpen voor Instagram horen in de planner; print en e-mail hebben een eigen weg. */
export const isPlannable = (d: MarketingDesign) => templateByKey(d.template).channel === 'Instagram'

/** Een nieuwe post op basis van een ontwerp: titel, beeld, tekst, hashtags en het juiste formaat. */
export const postFromDesign = (d: MarketingDesign): Partial<SocialPost> => ({
  title: d.name,
  designId: d.id,
  caption: d.caption,
  hashtags: d.hashtags,
  platforms: d.template === 'story' ? ['Instagram-story'] : ['Instagram-post'],
  status: 'concept',
})

export type DesignPlan =
  | { kind: 'gepland'; date: string }
  | { kind: 'geplaatst'; date: string }
  | { kind: 'zonder-datum' }
  | { kind: 'niet-gepland' }

/** Waar dit ontwerp in de planning staat: eerstvolgende datum, anders de laatste keer geplaatst. */
export function designPlan(designId: string, posts: SocialPost[]): DesignPlan {
  const own = posts.filter((p) => p.designId === designId)
  const upcoming = own
    .filter((p) => p.status !== 'geplaatst' && p.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0]
  if (upcoming) return { kind: 'gepland', date: upcoming.date }
  if (own.some((p) => p.status !== 'geplaatst' && !p.date)) return { kind: 'zonder-datum' }
  const posted = own.filter((p) => p.status === 'geplaatst').sort((a, b) => b.date.localeCompare(a.date))[0]
  if (posted) return { kind: 'geplaatst', date: posted.date }
  return { kind: 'niet-gepland' }
}

/** Instagram-ontwerpen die nog nergens in de planning staan: klaar om in te plannen. */
export const unplannedDesigns = (designs: MarketingDesign[], posts: SocialPost[]) =>
  designs.filter((d) => isPlannable(d) && !posts.some((p) => p.designId === d.id))

/** Hoe ver vooruit we een vrije dag zoeken voordat we gewoon vandaag nemen. */
const LOOKAHEAD_DAYS = 14

/** De eerste dag vanaf `from` (standaard vandaag) waarop nog niets gepland staat. */
export function firstFreeDay(posts: SocialPost[], from: string = today()): string {
  const start = fromIso(from)
  for (let i = 0; i < LOOKAHEAD_DAYS; i++) {
    const day = iso(addDays(start, i))
    if (!posts.some((p) => p.date === day)) return day
  }
  return from
}
